import React, { useRef, useState, useEffect, ReactNode } from "react";

interface DropdownProps {
  trigger: ReactNode;
  children: ReactNode;
  align?: "left" | "right";
  className?: string;
  darkMode?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  trigger,
  children,
  align = "right",
  className,
  darkMode = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className={`relative inline-block ${className || ""}`} ref={ref}>
      <div
        onClick={() => setOpen((v) => !v)}
        className="cursor-pointer select-none"
      >
        {trigger}
      </div>
      {open && (
        <div
          className={`absolute z-50 mt-3 md:min-w-[280px] min-w-[220px]  md:w-auto md:translate-x-0 md:left-auto rounded-2xl shadow-2xl border backdrop-blur-l transition-all duration-300 ease-out ${
            darkMode
              ? "bg-[#2a2a3c]/95 border-white/10 text-white"
              : "bg-white/95 border-gray-200/50 text-gray-900"
          } ${
            align === "right" ? "right-0" : "left-0"
          } animate-in slide-in-from-top-2 fade-in duration-200`}
          style={{
            boxShadow: darkMode
              ? "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)"
              : "0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
          }}
        >
          {/* Dropdown Arrow */}
          <div
            className={`absolute -top-2 w-4 h-4 rotate-45 
              ${align === "right" ? "right-4 md:right-4 md:left-auto -translate-x-1/2 md:translate-x-0" : "left-4 md:left-4 left-1/2 md:left-auto -translate-x-1/2 md:translate-x-0"}

            ${
              darkMode
                ? "bg-[#232336]/95 border-l border-t border-white/10"
                : "bg-white/95 border-l border-t border-gray-200/50"
            }`}
          />

          {/* Content */}
          <div className="relative p-1">{children}</div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;